import {
  createContext,
  useCallback,
  useContext,
  useState,
  ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IRequest } from '../constants/interfaces';
import { getRequests } from '../api/Request.client';
import { queryKeys } from '../api/queryKeys';
import { SearchOptions } from '../constants/app';

interface RequestsContextType {
  requests: IRequest[];
  loading: boolean;
  error: string | null;
  fetchRequests: (params: SearchOptions) => Promise<void>;
  invalidateRequestsListCache: () => void;
}

const RequestsContext = createContext<RequestsContextType | undefined>(
  undefined
);

export function RequestsProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useState<SearchOptions | null>(null);

  const {
    data,
    isLoading: loading,
    error: queryError,
  } = useQuery({
    enabled: !!searchParams,
    queryFn: () => getRequests(searchParams as SearchOptions),
    queryKey: queryKeys.requests(searchParams as SearchOptions),
  });

  const requests = (data ?? []) as IRequest[];
  const error = queryError
    ? 'Failed to load requests. Please try again later.'
    : null;

  // Matches the existing imperative API that consumers call
  const fetchRequests = useCallback(async (params: SearchOptions) => {
    setSearchParams(params);
  }, []);

  const invalidateRequestsListCache = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['requests'] });
  }, [queryClient]);

  return (
    <RequestsContext.Provider
      value={{
        error,
        fetchRequests,
        invalidateRequestsListCache,
        loading,
        requests,
      }}
    >
      {children}
    </RequestsContext.Provider>
  );
}

export function useRequests() {
  const context = useContext(RequestsContext);
  if (context === undefined) {
    throw new Error('useRequests must be used within a RequestsProvider');
  }
  return context;
}
